import React, { useCallback, useEffect, useMemo } from 'react';

import classNames from 'classnames';

import ceil from 'lodash/ceil';
import range from 'lodash/range';

import BasicDropdown from '../BasicDropdown/BasicDropdown';
import Icon from '../icons/Icon';
import Label from '../Labels/Label';
import LinkButton from '../Labels/LinkButton';

import CaretLeftIcon from '../icons/CaretLeftIcon';
import CaretRightIcon from '../icons/CaretRightIcon';

import COLORS from '../../../constants/colors.constants';

const IconItem = ({ icon, onClick, disabled = false }) => (
  <LinkButton bold className="mx-2" onClick={onClick} disabled={disabled}>
    <Icon
      size={16}
      icon={icon}
      fill={disabled ? COLORS.border.darker : COLORS.brand}
    />
  </LinkButton>
)

const PaginationItem = ({ index, onItemClick, isSelected = false }) => {
  const onClick = useCallback(() => onItemClick(index), [index, onItemClick])
  return (
    <LinkButton
      className={classNames(
        'mx-2',
        { 'text-brand hover:text-border-dark': !isSelected },
        { 'text-border-darker': isSelected },
      )}
      disabled={isSelected}
      onClick={onClick}
    >
      {index}
    </LinkButton>
  )
}

const EllipsisItem = ({ showEllipsis = false }) => showEllipsis ? <Label className="mx-2">&hellip;</Label> : null

const calculateStartValue = (selectedPage, numberOfPages) => {
  // If we have 5 pages, or we're not against the boundary
  if (numberOfPages < 6 || selectedPage - 2 < 1) {
    // 1 is reserved for the First Page button, so use 2
    return 1;
  }

  return selectedPage - 2;
}

const calculateEndValue = (selectedPage, numberOfPages) => {
  if (numberOfPages < 6 || selectedPage + 2 > numberOfPages) {
    return numberOfPages;
  }

  return selectedPage + 2;
}

const PaginationOptions = ({ selectedPage, numberOfPages, onItemClick }) => {
  const start = calculateStartValue(selectedPage, numberOfPages);
  const end = calculateEndValue(selectedPage, numberOfPages);
  const itemsToShow = range(start > 1 ? start : 2, end < numberOfPages ? end + 1 : numberOfPages);
  const isSinglePage = numberOfPages < 2;

  const showLeftEllipsis = numberOfPages > 5 && start > 2;
  const showRightEllipsis = numberOfPages > 5 && end < numberOfPages - 1;

  return (
    <div className="flex items-center my-1" id="placeholder-page-indeces">
      <IconItem
        key="pagination-page-prev"
        icon={CaretLeftIcon}
        onClick={() => onItemClick(selectedPage - 1)}
        disabled={selectedPage === 1}
      />
      <div className="flex items-center justify-evenly mx-2">
        {
          !isSinglePage && (
            <PaginationItem
              key="pagination-page-first"
              index={1}
              onItemClick={onItemClick}
              isSelected={selectedPage === 1}
            />
          )
        }
        <EllipsisItem showEllipsis={showLeftEllipsis} />
        {
          !isSinglePage ? itemsToShow?.map(val => (
            <PaginationItem
              key={`pagination-page-${val}`}
              index={val}
              onItemClick={onItemClick}
              isSelected={val === selectedPage}
            />
          )) : (
            <PaginationItem
              key="pagination-page-only"
              index={1}
              onItemClick={onItemClick}
              isSelected
            />
          )
        }
        <EllipsisItem showEllipsis={showRightEllipsis} />
        {
          !isSinglePage && (
            <PaginationItem
              key="pagination-page-last"
              index={numberOfPages}
              onItemClick={onItemClick}
              isSelected={selectedPage === numberOfPages}
            />
          )
        }
      </div>
      <IconItem
        key="pagination-page-next"
        icon={CaretRightIcon}
        onClick={() => onItemClick(selectedPage + 1)}
        disabled={selectedPage === numberOfPages}
      />
    </div>
  )
}

export const PaginationRow = ({
  className = '',
  selectedPage = 0,
  numberOfResults = 0,
  resultsPerPage = 25,
  resultsPerPageValues = [10, 25, 50, 100],
  formatResultsCount = val => `${val === 0 ? 'No' : val} result${val !== 1 ? 's' : ''}`,
  setSelectedPage = () => {},
  setResultsPerPage = () => {},
}) => {
  const onItemClick = useCallback((pageNum) => {
    setSelectedPage(pageNum);
  }, [setSelectedPage]);

  const resultsPerPageOptions = useMemo(() => resultsPerPageValues.map(val => ({ id: val, label: val })), [resultsPerPageValues]);

  const numberOfPages = ceil(numberOfResults / resultsPerPage);

  // Automatically reroute to the maximum page val if the value goes over
  useEffect(() => {
    if (selectedPage > numberOfPages) {
      setSelectedPage(numberOfPages);
    }
  }, [numberOfPages, selectedPage, setSelectedPage])

  return (
    <div className={classNames(
      'flex items-center justify-between w-full',
      className,
    )}>
      <Label>{formatResultsCount(numberOfResults)}</Label>
      <PaginationOptions
        selectedPage={selectedPage}
        numberOfPages={numberOfPages}
        onItemClick={onItemClick}
      />
      <div className="flex items-center">
        <BasicDropdown
          values={resultsPerPageOptions}
          selectedValue={resultsPerPage}
          onValueSelected={setResultsPerPage}
          buttonLabelSize="md"
          dropdownLabel={null}
          hideLabelInSmallViewports={false}
        />
        <Label className="ml-2">per page</Label>
      </div>
    </div>
  )
}


export default PaginationRow;
